import { DollarSign, Download, Link2, RefreshCw } from "lucide-react";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTheme } from "../../../../contexts/ThemeContext";
import { useToast } from "../../../../contexts/ToastContext";
import contractsV2Service from "../../../../services/contractsV2Service";
import essentialInstrumentsService from "../../../../services/essentialInstrumentsService";
import { Contract } from "../../../../types/contract";
import { EssentialOptionContract } from "../../../../types/essentialInstruments";
import { buildCSV, triggerCSVDownload } from "../../../../utils/csvExport";
import EssentialOptionPriceFactorModal from "./EssentialOptionPriceFactorModal";
import ContractUsageModal from "../../Contracts/components/ContractUsageModal";

const HYDRATION_BATCH_SIZE = 100;

interface HydratedOptionRow {
  key: string; // contractId:exchange composite key
  essentialContractId: number;
  essentialExchange: string;
  contract: Contract | null;
}

const EssentialOptionContractsTab: React.FC = () => {
  const { theme } = useTheme();
  const isDarkMode = theme === "dark";
  const toast = useToast();

  const [rows, setRows] = useState<HydratedOptionRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingProgress, setLoadingProgress] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [missingCount, setMissingCount] = useState(0);
  const [priceFactorRow, setPriceFactorRow] = useState<HydratedOptionRow | null>(null);
  const [usageRow, setUsageRow] = useState<HydratedOptionRow | null>(null);

  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  // Sorting
  const [sortColumn, setSortColumn] = useState<string>("essentialContractId");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("asc");

  const fetchOptionContracts = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      setMissingCount(0);
      setLoadingProgress("Fetching essential option contracts...");

      const response = await essentialInstrumentsService.getEssentialOptionContracts();
      const essentialOptions: EssentialOptionContract[] = response.contracts;

      if (essentialOptions.length === 0) {
        setRows([]);
        setLoadingProgress("");
        return;
      }

      // Extract unique contractIds for hydration
      const uniqueContractIds = [...new Set(essentialOptions.map((o) => o.contractId))];

      // Chunk and hydrate
      const chunks: number[][] = [];
      for (let i = 0; i < uniqueContractIds.length; i += HYDRATION_BATCH_SIZE) {
        chunks.push(uniqueContractIds.slice(i, i + HYDRATION_BATCH_SIZE));
      }

      const contractMap = new Map<number, Contract>();

      for (let i = 0; i < chunks.length; i++) {
        setLoadingProgress(`Hydrating contracts... ${i + 1}/${chunks.length} batches`);
        try {
          const batchResult = await contractsV2Service.findContractsByConIds(chunks[i]);
          batchResult.forEach((c) => contractMap.set(c.conId, c));
        } catch (batchErr) {
          console.error(`Option batch ${i + 1} hydration failed:`, batchErr);
        }
      }

      // Join hydrated data back onto each essential row
      const hydratedRows: HydratedOptionRow[] = essentialOptions.map((opt) => ({
        key: `${opt.contractId}:${opt.exchange}`,
        essentialContractId: opt.contractId,
        essentialExchange: opt.exchange,
        contract: contractMap.get(opt.contractId) || null,
      }));

      const missing = hydratedRows.filter((r) => r.contract === null).length;
      setMissingCount(missing);

      if (missing > 0) {
        toast.warning(`${missing} option contract(s) could not be found in the contracts database`);
      }

      setRows(hydratedRows);
      setLoadingProgress("");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to fetch essential option contracts");
      setLoadingProgress("");
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    fetchOptionContracts();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Client-side sorting
  const sortedRows = useMemo(() => {
    const sorted = [...rows];
    sorted.sort((a, b) => {
      let aVal: string | number | null;
      let bVal: string | number | null;

      if (sortColumn === "essentialContractId") {
        aVal = a.essentialContractId;
        bVal = b.essentialContractId;
      } else if (sortColumn === "essentialExchange") {
        aVal = a.essentialExchange;
        bVal = b.essentialExchange;
      } else {
        aVal = a.contract ? (a.contract[sortColumn as keyof Contract] as string | number | null) : null;
        bVal = b.contract ? (b.contract[sortColumn as keyof Contract] as string | number | null) : null;
      }

      if (aVal === null || aVal === undefined) return 1;
      if (bVal === null || bVal === undefined) return -1;

      let comparison = 0;
      if (typeof aVal === "number" && typeof bVal === "number") {
        comparison = aVal - bVal;
      } else {
        comparison = String(aVal).localeCompare(String(bVal));
      }

      return sortDirection === "asc" ? comparison : -comparison;
    });
    return sorted;
  }, [rows, sortColumn, sortDirection]);

  // Pagination
  const totalItems = sortedRows.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / pageSize));
  const paginatedRows = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return sortedRows.slice(start, start + pageSize);
  }, [sortedRows, currentPage, pageSize]);

  useEffect(() => {
    if (currentPage > totalPages) {
      setCurrentPage(1);
    }
  }, [currentPage, totalPages]);

  const handleDownloadCSV = useCallback(() => {
    const headers = [
      "contractId",
      "essentialExchange",
      "symbol",
      "longName",
      "secType",
      "currency",
      "exchange",
      "primaryExchange",
    ];

    const csvRows = sortedRows.map((row) => [
      String(row.essentialContractId),
      row.essentialExchange,
      row.contract?.symbol ?? "",
      row.contract?.longName ?? "",
      row.contract?.secType ?? "",
      row.contract?.currency ?? "",
      row.contract?.exchange ?? "",
      row.contract?.primaryExchange ?? "",
    ]);

    const csv = buildCSV(headers, csvRows);
    const filename = `essential_option_contracts_${new Date().toISOString().split("T")[0]}.csv`;
    triggerCSVDownload(csv, filename);
  }, [sortedRows]);

  const handleSort = (column: string) => {
    if (sortColumn === column) {
      setSortDirection((prev) => (prev === "asc" ? "desc" : "asc"));
    } else {
      setSortColumn(column);
      setSortDirection("asc");
    }
    setCurrentPage(1);
  };

  const sortIndicator = (column: string) => {
    if (sortColumn !== column) return null;
    return sortDirection === "asc" ? " \u25B2" : " \u25BC";
  };

  if (loading && rows.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div
            className={`animate-spin rounded-full h-8 w-8 border-b-2 ${
              isDarkMode ? "border-rp-amber" : "border-blue-600"
            } mx-auto`}
          />
          <p className={`mt-3 text-sm ${isDarkMode ? "text-rp-grey/50" : "text-gray-600"}`}>
            {loadingProgress || "Loading option contracts..."}
          </p>
        </div>
      </div>
    );
  }

  const columns: { key: string; label: string; sortable: boolean }[] = [
    { key: "essentialContractId", label: "Contract ID", sortable: true },
    { key: "essentialExchange", label: "Essential Exchange", sortable: true },
    { key: "symbol", label: "Symbol", sortable: true },
    { key: "longName", label: "Name", sortable: true },
    { key: "secType", label: "Type", sortable: true },
    { key: "currency", label: "Currency", sortable: true },
    { key: "exchange", label: "Contract Exchange", sortable: true },
    { key: "primaryExchange", label: "Primary Exchange", sortable: true },
    { key: "actions", label: "Actions", sortable: false },
  ];

  return (
    <div className="h-full flex flex-col">
      {/* Toolbar */}
      <div className="flex items-center justify-between mb-4">
        <div className={`text-sm ${isDarkMode ? "text-rp-grey/50" : "text-gray-600"}`}>
          {totalItems} option contracts
          {missingCount > 0 && (
            <span className="ml-2 text-yellow-600 dark:text-yellow-400">
              ({missingCount} not found in contracts DB)
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={handleDownloadCSV}
            disabled={rows.length === 0}
            className={`flex items-center gap-2 px-3 py-2 text-sm rounded-md border transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
              isDarkMode
                ? "border-rp-separator text-rp-grey/70 hover:bg-rp-surface-raised"
                : "border-gray-300 text-gray-700 hover:bg-gray-50"
            }`}
          >
            <Download className="w-4 h-4" />
            Download CSV
          </button>
          <button
            onClick={fetchOptionContracts}
            disabled={loading}
            className={`flex items-center gap-2 px-3 py-2 text-sm rounded-md border transition-colors ${
              isDarkMode
                ? "border-rp-separator text-rp-grey/70 hover:bg-rp-surface-raised"
                : "border-gray-300 text-gray-700 hover:bg-gray-50"
            }`}
          >
            <RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
            Refresh
          </button>
        </div>
      </div>

      {error && (
        <div className="mb-4 bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative text-sm">
          {error}
        </div>
      )}

      {/* Table */}
      <div className={`flex-1 overflow-auto border rounded-lg ${isDarkMode ? "border-rp-separator" : "border-gray-200"}`}>
        <table className={`min-w-full divide-y ${isDarkMode ? "divide-rp-separator" : "divide-gray-200"}`}>
          <thead className={`${isDarkMode ? "bg-rp-surface-sunken" : "bg-gray-50"} sticky top-0 z-10`}>
            <tr>
              {columns.map((col) => (
                <th
                  key={col.key}
                  className={`px-4 py-3 text-left text-xs font-medium uppercase tracking-wider whitespace-nowrap ${
                    isDarkMode ? "text-rp-grey/70" : "text-gray-500"
                  } ${
                    col.sortable
                      ? isDarkMode
                        ? "cursor-pointer select-none hover:bg-rp-surface-raised"
                        : "cursor-pointer select-none hover:bg-gray-600/20"
                      : ""
                  }`}
                  onClick={() => col.sortable && handleSort(col.key)}
                >
                  {col.label}
                  {col.sortable && sortIndicator(col.key)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className={`divide-y ${isDarkMode ? "divide-rp-separator bg-rp-surface" : "divide-gray-200 bg-white"}`}>
            {paginatedRows.length === 0 ? (
              <tr>
                <td
                  colSpan={columns.length}
                  className={`px-4 py-8 text-center text-sm ${isDarkMode ? "text-rp-grey/50" : "text-gray-500"}`}
                >
                  No essential option contracts configured
                </td>
              </tr>
            ) : (
              paginatedRows.map((row) => {
                const c = row.contract;
                return (
                  <tr key={row.key} className={isDarkMode ? "hover:bg-rp-surface-raised" : "hover:bg-gray-50"}>
                    <td className={`px-4 py-3 text-sm font-mono ${isDarkMode ? "text-rp-grey/70" : "text-gray-900"}`}>
                      {row.essentialContractId}
                    </td>
                    <td className={`px-4 py-3 text-sm ${isDarkMode ? "text-rp-grey/70" : "text-gray-900"}`}>
                      {row.essentialExchange}
                    </td>
                    <td className={`px-4 py-3 text-sm ${isDarkMode ? "text-rp-grey/70" : "text-gray-900"}`}>
                      {c?.symbol ?? <span className={isDarkMode ? "text-rp-grey/25" : "text-gray-400"}>--</span>}
                    </td>
                    <td className={`px-4 py-3 text-sm ${isDarkMode ? "text-rp-grey/70" : "text-gray-900"}`}>
                      {c?.longName ?? <span className={isDarkMode ? "text-rp-grey/25" : "text-gray-400"}>--</span>}
                    </td>
                    <td className={`px-4 py-3 text-sm ${isDarkMode ? "text-rp-grey/70" : "text-gray-900"}`}>
                      {c?.secType ?? <span className={isDarkMode ? "text-rp-grey/25" : "text-gray-400"}>--</span>}
                    </td>
                    <td className={`px-4 py-3 text-sm ${isDarkMode ? "text-rp-grey/70" : "text-gray-900"}`}>
                      {c?.currency ?? <span className={isDarkMode ? "text-rp-grey/25" : "text-gray-400"}>--</span>}
                    </td>
                    <td className={`px-4 py-3 text-sm ${isDarkMode ? "text-rp-grey/70" : "text-gray-900"}`}>
                      {c?.exchange ?? <span className={isDarkMode ? "text-rp-grey/25" : "text-gray-400"}>--</span>}
                    </td>
                    <td className={`px-4 py-3 text-sm ${isDarkMode ? "text-rp-grey/70" : "text-gray-900"}`}>
                      {c?.primaryExchange ?? <span className={isDarkMode ? "text-rp-grey/25" : "text-gray-400"}>--</span>}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-center gap-2">
                        <button
                          type="button"
                          onClick={() => setPriceFactorRow(row)}
                          className="inline-flex items-center gap-1.5 rounded-lg bg-rp-amber px-3 py-1.5 text-xs font-semibold text-white transition-colors hover:bg-yellow-600"
                          title="View option prices and factors"
                        >
                          <DollarSign className="h-4 w-4" />
                          Prices
                        </button>
                        <button
                          type="button"
                          onClick={() => setUsageRow(row)}
                          className="inline-flex items-center gap-1.5 rounded-lg border border-gray-200 bg-white px-3 py-1.5 text-xs font-semibold text-gray-700 transition-colors hover:border-gray-300 dark:border-rp-separator dark:bg-rp-surface dark:text-gray-300 dark:hover:border-gray-600"
                          title="View option usage"
                        >
                          <Link2 className="h-4 w-4" />
                          Usage
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className={`mt-4 flex items-center justify-between ${isDarkMode ? "text-rp-grey/50" : "text-gray-600"}`}>
        <div className="flex items-center gap-4 text-sm">
          <div className="flex items-center gap-2">
            <span>Show</span>
            <select
              value={pageSize}
              onChange={(e) => {
                setPageSize(Number(e.target.value));
                setCurrentPage(1);
              }}
              className="border rounded p-1 dark:bg-rp-surface dark:border-rp-separator"
            >
              <option value={20}>20</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
              <option value={500}>500</option>
              <option value={1000}>1000</option>
              <option value={totalItems || 99999}>All</option>
            </select>
          </div>
          <span>
            Showing {totalItems === 0 ? 0 : (currentPage - 1) * pageSize + 1} to{" "}
            {Math.min(currentPage * pageSize, totalItems)} of {totalItems}
          </span>
        </div>
        <div className="flex gap-2">
          <button
            onClick={() => setCurrentPage(1)}
            disabled={currentPage === 1}
            className={`px-2 py-1 text-sm rounded disabled:opacity-50 ${
              isDarkMode ? "hover:bg-rp-surface-raised" : "hover:bg-gray-100"
            }`}
          >
            First
          </button>
          <button
            onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
            disabled={currentPage === 1}
            className={`px-2 py-1 text-sm rounded disabled:opacity-50 ${
              isDarkMode ? "hover:bg-rp-surface-raised" : "hover:bg-gray-100"
            }`}
          >
            Prev
          </button>
          <span className="px-2 py-1 text-sm">
            Page {currentPage} of {totalPages}
          </span>
          <button
            onClick={() => setCurrentPage((prev) => Math.min(totalPages, prev + 1))}
            disabled={currentPage === totalPages}
            className={`px-2 py-1 text-sm rounded disabled:opacity-50 ${
              isDarkMode ? "hover:bg-rp-surface-raised" : "hover:bg-gray-100"
            }`}
          >
            Next
          </button>
        </div>
      </div>

      {priceFactorRow && (
        <EssentialOptionPriceFactorModal
          isOpen={true}
          onClose={() => setPriceFactorRow(null)}
          conId={priceFactorRow.essentialContractId}
          symbol={priceFactorRow.contract?.symbol ?? String(priceFactorRow.essentialContractId)}
        />
      )}

      {usageRow && (
        <ContractUsageModal
          isOpen={true}
          onClose={() => setUsageRow(null)}
          conId={usageRow.essentialContractId}
          symbol={usageRow.contract?.symbol ?? String(usageRow.essentialContractId)}
        />
      )}
    </div>
  );
};

export default EssentialOptionContractsTab;
